import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userAPI, walletAPI, authAPI } from '../../services/api';
import FirstLoginSetup from '../auth/FirstLoginSetup';
import TransactionPasswordModal from '../common/TransactionPasswordModal';
import '../../styles/admin/AdminDashboard.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const AdminDashboard = () => {
    useDesktopViewport();
    const [showFirstLogin, setShowFirstLogin] = useState(false);
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showExposureModal, setShowExposureModal] = useState(false);
    const [exposureLimit, setExposureLimit] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        email: '',
        phone: '',
        role: 'user'
    });
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalBalance: 0,
        totalExposure: 0,
        activeBets: 0
    });

    const loadUsers = async () => {
        try {
            const response = await userAPI.getAllUsers();
            if (response.success && response.data && Array.isArray(response.data.users)) {
                const usersWithPermissions = response.data.users.map(user => ({
                    ...user,
                    canPerformActions: user.parentId === currentUser?._id
                }));
                setUsers(usersWithPermissions);
            } else if (Array.isArray(response.data)) {
                const usersWithPermissions = response.data.map(user => ({
                    ...user,
                    canPerformActions: user.parentId === currentUser?._id
                }));
                setUsers(usersWithPermissions);
            } else if (Array.isArray(response)) {
                const usersWithPermissions = response.map(user => ({
                    ...user,
                    canPerformActions: user.parentId === currentUser?._id
                }));
                setUsers(usersWithPermissions);
            } else {
                console.error('Unexpected users response structure:', response);
                setUsers([]);
            }
        } catch (error) {
            console.error('Error loading users:', error);
            setUsers([]);
        }
    };

    const loadStats = async () => {
        try {
            const response = await userAPI.getAdminStats();
            if (response.success && response.data) {
                setStats(response.data);
            } else if (response.data) {
                setStats(response.data);
            } else {
                setStats(response);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const checkFirstLoginStatus = async () => {
        try {
            const response = await authAPI.checkFirstLogin();
            if (response.data.isFirstLogin && !response.data.transactionPasswordSet) {
                setShowFirstLogin(true);
            }
        } catch (error) {
            console.error('Error checking first login:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            await loadUsers();
            await loadStats();
            await checkFirstLoginStatus();
        };
        init();
    }, []);

    const handleFirstLoginComplete = () => {
        setShowFirstLogin(false);
        window.location.reload();
    };

    // Clear transaction token after each operation
    const clearTransactionToken = () => {
        localStorage.removeItem('transactionToken');
    };

    // Check if transaction password is required (always check, no session)
    const requireTransactionPassword = (action, data) => {
        // Always clear any existing token to force new verification each time
        clearTransactionToken();
        
        setPendingAction({ type: action, ...data });
        setShowTransactionModal(true);
        return false;
    };

    const handleTransactionVerified = (tempToken) => {
        setShowTransactionModal(false);
        localStorage.setItem('transactionToken', tempToken);
        
        // Execute pending action
        if (pendingAction) {
            switch (pendingAction.type) {
                case 'deposit':
                    executeDepositBalance(pendingAction.userId, pendingAction.amount);
                    break;
                case 'withdraw':
                    executeWithdrawBalance(pendingAction.userId, pendingAction.amount);
                    break;
                case 'exposure':
                    executeUpdateExposureLimit(pendingAction.userId, pendingAction.limit);
                    break;
                case 'toggleStatus':
                    executeToggleUserStatus(pendingAction.userId, pendingAction.currentStatus);
                    break;
                case 'toggleBetting':
                    executeToggleBettingStatus(pendingAction.userId, pendingAction.currentBetStatus);
                    break;
                case 'changePassword':
                    executeChangePassword(pendingAction.userId, pendingAction.newPassword);
                    break;
                case 'createUser':
                    executeCreateUser(pendingAction.userData);
                    break;
                default:
                    break;
            }
            setPendingAction(null);
        }
    };

    // ==================== USER MANAGEMENT FUNCTIONS ====================

    const executeToggleUserStatus = async (userId, currentStatus) => {
        try {
            await userAPI.toggleUserStatus(userId);
            await loadUsers();
            alert(`User ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully!`);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error toggling user status');
        }
    };

    const handleToggleUserStatus = (userId, currentStatus, canPerform) => {
        if (!canPerform) {
            alert('You can only modify users that you directly created.');
            return;
        }
        if (window.confirm(`Are you sure you want to ${currentStatus === 'active' ? 'deactivate' : 'activate'} this user?`)) {
            requireTransactionPassword('toggleStatus', { userId, currentStatus });
        }
    };

    const executeToggleBettingStatus = async (userId, currentBetStatus) => {
        try {
            await userAPI.toggleBettingStatus(userId);
            await loadUsers();
            alert(`Betting ${currentBetStatus ? 'disabled' : 'enabled'} for user!`);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error toggling betting status');
        }
    };

    const handleToggleBettingStatus = (userId, currentBetStatus, canPerform) => {
        if (!canPerform) {
            alert('You can only modify users that you directly created.');
            return;
        }
        if (window.confirm(`Are you sure you want to ${currentBetStatus ? 'disable' : 'enable'} betting for this user?`)) {
            requireTransactionPassword('toggleBetting', { userId, currentBetStatus });
        }
    };

    const executeUpdateExposureLimit = async (userId, limit) => {
        if (!limit || parseFloat(limit) < 0) {
            alert('Please enter a valid exposure limit');
            return;
        }
        try {
            await userAPI.updateExposureLimit(userId, parseFloat(limit));
            await loadUsers();
            alert('Exposure limit updated successfully!');
            setShowExposureModal(false);
            setExposureLimit('');
            setSelectedUser(null);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error updating exposure limit');
        }
    };

    const handleUpdateExposureLimit = (userId, limit, canPerform) => {
        if (!canPerform) {
            alert('You can only modify users that you directly created.');
            return;
        }
        requireTransactionPassword('exposure', { userId, limit });
    };

    const executeChangePassword = async (userId, password) => {
        if (!password || password.length < 4) {
            alert('Password must be at least 4 characters');
            return;
        }
        try {
            await userAPI.changeUserPassword(userId, password);
            alert('Password changed successfully! User will need to set a new transaction password on next login.');
            setShowPasswordModal(false);
            setNewPassword('');
            setSelectedUser(null);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error changing password');
        }
    };

    const handleChangePassword = (userId, password, canPerform) => {
        if (!canPerform) {
            alert('You can only modify users that you directly created.');
            return;
        }
        requireTransactionPassword('changePassword', { userId, newPassword: password });
    };

    // ==================== WALLET MANAGEMENT FUNCTIONS ====================

    const executeDepositBalance = async (userId, amount) => {
        if (!amount || parseFloat(amount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        try {
            await walletAPI.credit({ userId, amount: parseFloat(amount), description: 'Admin deposit' });
            await loadUsers();
            await loadStats();
            alert(`₹${amount} deposited successfully!`);
            setShowDepositModal(false);
            setDepositAmount('');
            setSelectedUser(null);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error depositing balance');
        }
    };

    const handleDeposit = (userId, amount, canPerform) => {
        if (!canPerform) {
            alert('You can only deposit to users that you directly created.');
            return;
        }
        requireTransactionPassword('deposit', { userId, amount });
    };

    const executeWithdrawBalance = async (userId, amount) => {
        if (!amount || parseFloat(amount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }
        try {
            await walletAPI.debit({ userId, amount: parseFloat(amount), description: 'Admin withdrawal' });
            await loadUsers();
            await loadStats();
            alert(`₹${amount} withdrawn successfully!`);
            setShowWithdrawModal(false);
            setWithdrawAmount('');
            setSelectedUser(null);
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error withdrawing balance');
        }
    };

    const handleWithdraw = (userId, amount, canPerform) => {
        if (!canPerform) {
            alert('You can only withdraw from users that you directly created.');
            return;
        }
        requireTransactionPassword('withdraw', { userId, amount });
    };

    // ==================== USER CREATION ====================

    const executeCreateUser = async (userData) => {
        try {
            await userAPI.createUser(userData);
            alert('User created successfully!');
            setShowAddModal(false);
            setNewUser({ username: '', password: '', email: '', phone: '', role: 'user' });
            await loadUsers();
            await loadStats();
            clearTransactionToken(); // Clear after operation
        } catch (error) {
            alert(error.response?.data?.message || 'Error creating user');
        }
    };

    const handleCreateUser = (e) => {
        e.preventDefault();
        requireTransactionPassword('createUser', { userData: newUser });
    };

    // ==================== MODAL HANDLERS ====================

    const openDepositModal = (userItem) => {
        if (!userItem.canPerformActions) {
            alert('You can only deposit to users that you directly created.');
            return;
        }
        setSelectedUser(userItem);
        setDepositAmount('');
        setShowDepositModal(true);
    };

    const openWithdrawModal = (userItem) => {
        if (!userItem.canPerformActions) {
            alert('You can only withdraw from users that you directly created.');
            return;
        }
        setSelectedUser(userItem);
        setWithdrawAmount('');
        setShowWithdrawModal(true);
    };

    const openExposureModal = (userItem) => {
        if (!userItem.canPerformActions) {
            alert('You can only modify users that you directly created.');
            return;
        }
        setSelectedUser(userItem);
        setExposureLimit(userItem.exposureLimit || '');
        setShowExposureModal(true);
    };

    const openPasswordModal = (userItem) => {
        if (!userItem.canPerformActions) {
            alert('You can only modify users that you directly created.');
            return;
        }
        setSelectedUser(userItem);
        setNewPassword('');
        setShowPasswordModal(true);
    };

    const handleDepositSubmit = () => {
        if (selectedUser && depositAmount) {
            setShowDepositModal(false);
            handleDeposit(selectedUser._id, depositAmount, selectedUser.canPerformActions);
        }
    };

    const handleWithdrawSubmit = () => {
        if (selectedUser && withdrawAmount) {
            setShowWithdrawModal(false);
            handleWithdraw(selectedUser._id, withdrawAmount, selectedUser.canPerformActions);
        }
    };

    const handleExposureSubmit = () => {
        if (selectedUser && exposureLimit) {
            setShowExposureModal(false);
            handleUpdateExposureLimit(selectedUser._id, exposureLimit, selectedUser.canPerformActions);
        }
    };

    const handlePasswordSubmit = () => {
        if (selectedUser && newPassword) {
            setShowPasswordModal(false);
            handleChangePassword(selectedUser._id, newPassword, selectedUser.canPerformActions);
        }
    };

    // Rest of your component remains the same (formatCurrency, getRoleBadgeClass, etc.)
    // ... (keep all your existing helper functions and return statement)

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const getRoleBadgeClass = (role) => {
        const classes = {
            admin: 'badge-danger',
            super_master: 'badge-warning',
            master: 'badge-info',
            agent: 'badge-primary',
            user: 'badge-secondary'
        };
        return classes[role] || 'badge-secondary';
    };

    const getRoleIcon = (role) => {
        const icons = {
            admin: '👑',
            super_master: '⭐',
            master: '🔷',
            agent: '🔶',
            user: '👤'
        };
        return icons[role] || '📊';
    };

    const calculateProfitLoss = (userItem) => {
        const totalDeposits = userItem.totalDeposits || 0;
        const totalWithdrawals = userItem.totalWithdrawals || 0;
        const totalWins = userItem.totalWins || 0;
        const totalLosses = userItem.totalLosses || 0;
        return (totalWins + totalDeposits) - (totalLosses + totalWithdrawals);
    };

    const filteredUsers = Array.isArray(users) ? users.filter(userItem => {
        if (filterStatus !== 'all' && userItem.status !== filterStatus) return false;
        if (searchTerm && !userItem.username?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        return true;
    }) : [];

    const activeCount = Array.isArray(users) ? users.filter(u => u.status === 'active').length : 0;
    const inactiveCount = Array.isArray(users) ? users.filter(u => u.status === 'inactive').length : 0;

    if (loading && showFirstLogin === null) {
        return <div className="loading">Loading dashboard...</div>;
    }

    if (showFirstLogin) {
        return <FirstLoginSetup onComplete={handleFirstLoginComplete} />;
    }

    return (
        // Your existing JSX return remains the same
        <div className="admin-dashboard">
            {/* Transaction Password Modal */}
            {showTransactionModal && (
                <TransactionPasswordModal 
                    onVerified={handleTransactionVerified}
                    onClose={() => setShowTransactionModal(false)}
                    action={pendingAction?.type || 'perform this action'}
                />
            )}



            {/* Filter and Add Account Row */}
            <div className="filter-section">
                <div className="filter-tabs">
                    <button 
                        className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        All ({users.length})
                    </button>
                    <button 
                        className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('active')}
                    >
                        Active ({activeCount})
                    </button>
                    <button 
                        className={`filter-btn ${filterStatus === 'inactive' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('inactive')}
                    >
                        Inactive ({inactiveCount})
                    </button>
                </div>
                <button onClick={() => setShowAddModal(true)} className="add-account-btn">
                    ➕ Add Account
                </button>
            </div>

            {/* Search Bar */}
            <div className="search-section">
                <input
                    type="text"
                    placeholder="🔍 Search by username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            {/* Clients Table */}
<div className="table-wrapper">
  <table className="clients-table">
    <thead>
      <tr>
        <th>Username</th>
        <th>Balance</th>
        <th>Current Exposure</th>
        <th>Available Balance</th> 
        <th>Exposure Limit</th>
        <th>Status</th>
        <th>Bet Status</th>
        <th>Account Type</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {filteredUsers.map((userItem) => {
        const profitLoss = calculateProfitLoss(userItem);
        const currentExposure = userItem.currentExposure || 0;
        const availableBalance = userItem.balance - currentExposure;   // ← compute
        const canPerform = userItem.canPerformActions === true;

        return (
          <tr key={userItem._id} className={!canPerform ? 'indirect-user' : ''}>
            <td className="username-cell">
            <span className="username-button">   {/* ← wrapper */}
                
                <span>{userItem.username}</span>
                
            </span>
            </td>
            <td className="balance">{formatCurrency(userItem.balance)}</td>
            <td className="exposure">{formatCurrency(currentExposure)}</td>
            <td className="available-balance">   {/* ← NEW column */}
              {formatCurrency(availableBalance)}
            </td>
            <td>{formatCurrency(userItem.exposureLimit) || 'Unlimited'}</td>
            <td>
              <button
                className={`status-toggle ${userItem.status}`}
                onClick={() => handleToggleUserStatus(userItem._id, userItem.status, canPerform)}
                disabled={!canPerform}
              >
                {userItem.status === 'active' ? 'Active' : 'Inactive'}
              </button>
            </td>
            <td>
              <button
                className={`bet-toggle ${userItem.bettingEnabled ? 'on' : 'off'}`}
                onClick={() => handleToggleBettingStatus(userItem._id, userItem.bettingEnabled, canPerform)}
                disabled={!canPerform}
              >
                {userItem.bettingEnabled ? 'On' : 'Off'}
              </button>
            </td>
            <td >
            {userItem.role.replace('_', ' ').toUpperCase()}
            </td>
            <td className="actions-cell">
              {/* action buttons unchanged */}
              <button
                onClick={() => openDepositModal(userItem)}
                className={`action-btn deposit ${!canPerform ? 'disabled' : ''}`}
                title={!canPerform ? 'Can only deposit to users you directly created' : 'Deposit'}
                disabled={!canPerform}
              >
                💰
              </button>
              <button
                onClick={() => openWithdrawModal(userItem)}
                className={`action-btn withdraw ${!canPerform ? 'disabled' : ''}`}
                title={!canPerform ? 'Can only withdraw from users you directly created' : 'Withdraw'}
                disabled={!canPerform}
              >
                💸
              </button>
              <button
                onClick={() => openExposureModal(userItem)}
                className={`action-btn exposure ${!canPerform ? 'disabled' : ''}`}
                title={!canPerform ? 'Can only modify users you directly created' : 'Set Exposure Limit'}
                disabled={!canPerform}
              >
                ⚖️
              </button>
              <button
                onClick={() => openPasswordModal(userItem)}
                className={`action-btn password ${!canPerform ? 'disabled' : ''}`}
                title={!canPerform ? 'Can only modify users you directly created' : 'Change Password'}
                disabled={!canPerform}
              >
                🔒
              </button>
              {userItem.role !== 'user' && (
                <button
                  onClick={() => navigate(`/admin/downline/${userItem._id}`)}
                  className="action-btn downline"
                  title="View Downline"
                >
                  👥
                </button>
              )}
            </td>
          </tr>
        );
      })}
      {filteredUsers.length === 0 && (
        <tr>
          <td colSpan="10" className="text-center">   {/* ← colSpan updated to 10 */}
            No clients found
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

            {/* All modals remain the same */}
            {/* Create User Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Account</h2>
                        <form onSubmit={handleCreateUser}>
                            <input type="text" placeholder="Username *" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} required />
                            <input type="password" placeholder="Password *" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required />
                            <input type="email" placeholder="Email *" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required />
                            <input type="tel" placeholder="Phone *" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} required />
                            <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})}>
                                <option value="user">User</option>
                                <option value="agent">Agent</option>
                                <option value="master">Master</option>
                                <option value="super_master">Super Master</option>
                            </select>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                                <button type="submit">Create Account</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Deposit Modal */}
            {showDepositModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowDepositModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Deposit to {selectedUser.username}</h2>
                        <p>Current Balance: {formatCurrency(selectedUser.balance)}</p>
                        <input 
                            type="number" 
                            placeholder="Amount" 
                            value={depositAmount}
                            onChange={(e) => setDepositAmount(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button onClick={() => setShowDepositModal(false)}>Cancel</button>
                            <button onClick={handleDepositSubmit}>Deposit</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Withdraw Modal */}
            {showWithdrawModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowWithdrawModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Withdraw from {selectedUser.username}</h2>
                        <p>Current Balance: {formatCurrency(selectedUser.balance)}</p>
                        <input 
                            type="number" 
                            placeholder="Amount" 
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button onClick={() => setShowWithdrawModal(false)}>Cancel</button>
                            <button onClick={handleWithdrawSubmit}>Withdraw</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Exposure Limit Modal */}
            {showExposureModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowExposureModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Set Exposure Limit for {selectedUser.username}</h2>
                        <input 
                            type="number" 
                            placeholder="Exposure Limit (0 for unlimited)" 
                            value={exposureLimit}
                            onChange={(e) => setExposureLimit(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button onClick={() => setShowExposureModal(false)}>Cancel</button>
                            <button onClick={handleExposureSubmit}>Update</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Modal */}
            {showPasswordModal && selectedUser && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Change Password for {selectedUser.username}</h2>
                        <input 
                            type="password" 
                            placeholder="New Password" 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button onClick={() => setShowPasswordModal(false)}>Cancel</button>
                            <button onClick={handlePasswordSubmit}>Change</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;